import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import notifications from 'notifications';
import {FilterKey, GLOBAL_SEARCH_URL, Operator, SortKey, keyToEmptyFilter} from 'search/SearchConstants';
import {switchToStudyItemsTab} from 'studies/workspace/StudySearchHelpers';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextValue from 'ui-library/TextValue';
import * as DateUtil from 'util/DateUtil';
import * as net from 'util/Net';

const NUM_ANALYSES_CHIPS_TO_DISPLAY = 3;

type AnalysesData =
  | {status: 'loading'}
  | {status: 'error'}
  | {status: 'success'; analyses: Array<AnalysisGraphQL>; totalAnalyses: number};

export function PresentationalStudyAnalysisListView({
  studyId,
  data,
  showDate = true,
}: {
  studyId: string;
  data: AnalysesData;
  /** If true, the analysis creation date will be shown to the right of the analysis chip. */
  showDate?: boolean;
}): JSX.Element {
  if (data.status === 'error') {
    return <TextValue value="Unable to fetch analyses. Please refresh the page." isFaded={true} />;
  } else if (data.status === 'loading') {
    return <CenteredHorizontalThrobber />;
  }
  return (
    <div data-test-component="StudyAnalysisListView" data-test-element="chip-list">
      <FieldLabel label="Recently created" />
      {data.analyses.map((analysis, index) => (
        <div key={index} className={css(styles.chips)}>
          <BenchChip className={css(styles.analysisChip)} id={analysis.id} size={ChipSize.SMALL} />
          {showDate && (
            <span className={css(styles.createdText)}>
              <TextValue
                value={`Created ${DateUtil.readableTime(analysis.createdAt, {showTime: false})}`}
                isFaded={true}
              />
            </span>
          )}
        </div>
      ))}
      {data.totalAnalyses === 0 && <TextValue value="This study has no analyses." isFaded={true} />}
      {data.totalAnalyses > data.analyses.length && (
        <div className={css(styles.viewAllButton)}>
          <Button
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.TEXT}
            onClick={async () => {
              await switchToStudyItemsTab(studyId, [
                {
                  ...keyToEmptyFilter(FilterKey.TYPES),
                  value: [ModelTypeConstants.ANALYSIS],
                },
              ]);
            }}
            icon="open"
            text={`View all (${data.totalAnalyses})`}
          />
        </div>
      )}
    </div>
  );
}

export default function StudyAnalysisListView(props: {studyId: string; showDate?: boolean}): JSX.Element {
  const [data, setData] = useState<AnalysesData>({status: 'loading'});
  const mostRecentSearchIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isUnmounted = false;

    const isStaleSearch = (key: string): boolean => isUnmounted || mostRecentSearchIdRef.current !== key;

    const key = uuidv4();
    mostRecentSearchIdRef.current = key;
    net
      .postJSON({
        url: GLOBAL_SEARCH_URL,
        data: {
          query: '',
          filters: [
            {key: FilterKey.IN_STUDY, operator: Operator.IS, value: [props.studyId]},
            {key: FilterKey.TYPES, operator: Operator.IS_ONE_OF, value: [ModelTypeConstants.ANALYSIS]},
            {key: FilterKey.ARCHIVE_PURPOSES, operator: Operator.IS_ONE_OF, value: ['NOT_ARCHIVED']},
          ],
          limit: NUM_ANALYSES_CHIPS_TO_DISPLAY,
          offset: 0,
          sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
        },
      })
      .done((resp, status) => {
        if (isStaleSearch(key)) {
          return;
        }
        setData({status: 'success', analyses: Object.values(resp.analysesById), totalAnalyses: resp.total});
      })
      .fail((resp) => {
        if (isStaleSearch(key)) {
          return;
        }
        setData({status: 'error'});
        notifications.notifyError(resp);
      })
      .always(() => {
        if (isStaleSearch(key)) {
          return;
        }
        mostRecentSearchIdRef.current = null;
      });
    setData({status: 'loading'});
    return () => {
      isUnmounted = true;
    };
  }, [props.studyId]);

  return (
    <PresentationalStudyAnalysisListView studyId={props.studyId} data={data} showDate={props.showDate} />
  );
}

const styles = StyleSheet.create({
  analysisChip: {
    marginBottom: '5px',
  },
  viewAllButton: {
    marginLeft: '10px',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  createdText: {
    fontVariantNumeric: 'tabular-nums',
    flex: 'none',
  },
});
